import type { GetPriorityImpactTurnoverImpactResponse } from 'src/types/priority-impact';

type TurnoverImpactChange = GetPriorityImpactTurnoverImpactResponse['upgraded'][number];
type TurnoverImpactChangeDesc = Pick<TurnoverImpactChange, 'prevPriorityDesc' | 'currPriorityDesc'>;

// Fixed order of all possible upgraded/downgraded priority transitions, as returned by the API when every
// transition has data. Used to keep the table order stable and to fill in missing transitions with zeros.
const UPGRADED_ORDER: TurnoverImpactChangeDesc[] = [
  { prevPriorityDesc: 'Low + Very Low', currPriorityDesc: 'High + Very High' },
  { prevPriorityDesc: 'Neutral', currPriorityDesc: 'High + Very High' },
  { prevPriorityDesc: 'Low + Very Low', currPriorityDesc: 'Neutral' },
  { prevPriorityDesc: 'High', currPriorityDesc: 'Very High' },
  { prevPriorityDesc: 'Very Low', currPriorityDesc: 'Low' },
];

const DOWNGRADED_ORDER: TurnoverImpactChangeDesc[] = [
  { prevPriorityDesc: 'High + Very High', currPriorityDesc: 'Low + Very Low' },
  { prevPriorityDesc: 'High + Very High', currPriorityDesc: 'Neutral' },
  { prevPriorityDesc: 'Neutral', currPriorityDesc: 'Low + Very Low' },
  { prevPriorityDesc: 'Very High', currPriorityDesc: 'High' },
  { prevPriorityDesc: 'Low', currPriorityDesc: 'Very Low' },
];

const sortAndFillChanges = (
  changes: TurnoverImpactChange[],
  order: TurnoverImpactChangeDesc[],
): TurnoverImpactChange[] =>
  order.map(({ prevPriorityDesc, currPriorityDesc }) => {
    const found = changes.find(
      (change) => change.prevPriorityDesc === prevPriorityDesc && change.currPriorityDesc === currPriorityDesc,
    );

    return (
      found ?? {
        prevPriorityDesc,
        currPriorityDesc,
        retieredGames: 0,
        prevTurnover: 0,
        currTurnover: 0,
        turnoverDelta: 0,
        turnoverDeltaPercent: 0,
      }
    );
  });

export const transformGetPriorityImpactTurnoverImpactResponse: (
  res: GetPriorityImpactTurnoverImpactResponse,
) => GetPriorityImpactTurnoverImpactResponse = (res) => ({
  ...res,
  upgraded: sortAndFillChanges(res.upgraded, UPGRADED_ORDER),
  downgraded: sortAndFillChanges(res.downgraded, DOWNGRADED_ORDER),
});
